import type { EventOccurrenceViewModel } from '@trinnvis/dabih-api-client';
import { checklistKeyForEventOccurrence, checklistSaveCoordinator } from 'src/utilities/checklist-save-coordinator';

/**
 * Beskytter pågående sjekkliste-lagringer mot stale server-snapshots (issue #13248).
 *
 * Innkommende eventOccurrence-data (Ably-ekko, liste-refetch, bakgrunnsoppdatering)
 * kan være hentet før siste PUT landet. Så lenge koordinatoren har lokalt innhold
 * for en occurrence (skriving pågår eller innenfor grace-vinduet), erstattes
 * `description` i innkommende data med det lokale innholdet — alle andre felter
 * tas ferske fra serveren. Reducerne forblir urørte.
 */

function overlayOccurrence(item: EventOccurrenceViewModel): EventOccurrenceViewModel {
  const localContent = checklistSaveCoordinator.getLocalContent(checklistKeyForEventOccurrence(item.uuid));
  if (localContent === undefined || item.description === localContent) {
    return item;
  }
  return { ...item, description: localContent };
}

// Path-format: /organizations/{id}/eventOccurrences/{uuid} — samme parsing som
// updateLevelOne i reducers.ts (p[3] = entityType).
function isEventOccurrencePath(path: string): boolean {
  return path.split('/')[3] === 'eventOccurrences';
}

export const checklistPendingMiddleware = () => (next) => (action) => {
  if (action.type === 'LOADED_EVENT_OCCURRENCES') {
    const occurrences: EventOccurrenceViewModel[] = action.eventOccurrences;
    let changed = false;
    const overlaid = occurrences.map((o) => {
      const result = overlayOccurrence(o);
      if (result !== o) {
        changed = true;
      }
      return result;
    });
    if (changed) {
      return next({ ...action, eventOccurrences: overlaid });
    }
  } else if (action.type === 'UPDATE_ITEM') {
    const message: { path: string; item?: EventOccurrenceViewModel } = action.message;
    if (message?.item?.uuid !== undefined && isEventOccurrencePath(message.path)) {
      const overlaid = overlayOccurrence(message.item);
      if (overlaid !== message.item) {
        return next({ ...action, message: { ...message, item: overlaid } });
      }
    }
  }
  return next(action);
};
